import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import EmailIcon from "@mui/icons-material/Email";
import UnsubscribeIcon from "@mui/icons-material/Unsubscribe";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { SelectedMeeting } from "../../../pages/notes/meetingNotes";
import EditableMeetingInfo from "../EditableMeetingInfo";
import ConfirmationPanel from "../../confirmationPanel";
import config from "../../../config";
import { ApiUser, Meeting, MeetingStatus, UserRole } from "../../../models";
import {
  MeetingItem,
  MeetingItemStatus,
  MeetingItemType,
} from "../../../models/meetingItem";
import { bulkCreateUsers } from "../../../services/userService";
import { uploadMeetingMinutes } from "../../../services/attachmentService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  formatLocalDateDisplay,
  formatMeetingTimeRange,
} from "../../../util/meetingDateTime";

interface DistributeStepProps {
  onBack: () => void;
  onDistributeComplete?: () => void;
  selectedMeeting: SelectedMeeting | null;
  items: MeetingItem[];
  distributeRef?: React.MutableRefObject<(() => void) | null>;
  onMeetingChange: (meeting: SelectedMeeting) => void;
  onMeetingStatusChange: (
    status: MeetingStatus,
    updates?: Partial<Meeting>,
  ) => Promise<boolean>;
  onError: (message: string, error?: unknown) => void;
}

interface Participant {
  id: string;
  email: string;
  label?: string;
  emailDistribution: "to" | "cc" | "none";
  accessRights: boolean;
  isAuthor?: boolean;
}

const isEmailFormat = (value: string) => value.includes("@");

const sectionLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  color: "#64748B",
  mb: 1.5,
};

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#242C63" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#242C63",
  },
};

/**
 * Generate PDF meeting minutes
 */
const getMeetingItemTypeLabel = (t: TFunction, itemType?: string) => {
  if (itemType === MeetingItemType.Decision || itemType === "decision") {
    return t("meetingNotes.common.itemTypes.decision");
  }

  if (itemType === MeetingItemType.Info || itemType === "info") {
    return t("meetingNotes.common.itemTypes.info");
  }

  return t("meetingNotes.common.itemTypes.task");
};

const getMeetingItemStatusLabel = (
  t: TFunction,
  status?: MeetingItemStatus | string,
) => {
  if (status === MeetingItemStatus.InProgress) {
    return t("item_status.inprogress");
  }

  if (status === MeetingItemStatus.Completed) {
    return t("item_status.completed");
  }

  if (status === MeetingItemStatus.Cancelled) {
    return t("item_status.cancelled");
  }

  if (status === MeetingItemStatus.OnHold) {
    return t("item_status.onhold");
  }

  return t("item_status.notstarted");
};

const buildMinutesDoc = (
  t: TFunction,
  meeting: SelectedMeeting | null,
  items: MeetingItem[],
  lang: string,
): { doc: jsPDF; fileName: string } => {
  const doc = new jsPDF({ orientation: "landscape" });
  const resolvedTitle =
    meeting?.title || t("meetingNotes.distribute.pdf.titleFallback");

  // Header
  let yPos = 18;

  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(36, 44, 99);
  doc.text(t("meetingNotes.distribute.pdf.heading"), 14, yPos);
  yPos += 10;

  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.text(resolvedTitle, 14, yPos);
  yPos += 8;

  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(
    `${t("meetingNotes.distribute.pdf.date")}: ${formatLocalDateDisplay(meeting?.date || "")}`,
    14,
    yPos,
  );
  yPos += 6;

  if (meeting?.startTime || meeting?.endTime) {
    doc.text(
      `${t("meetingNotes.distribute.pdf.time")}: ${formatMeetingTimeRange(meeting?.startTime, meeting?.endTime)}`,
      14,
      yPos,
    );
    yPos += 6;
  }

  doc.setFontSize(7);
  doc.setTextColor(156, 163, 175);
  doc.text(
    `${t("meetingNotes.distribute.pdf.notesCreated")}: ${new Date().toLocaleDateString(lang || undefined)}`,
    14,
    yPos,
  );
  yPos += 8;

  // Items table
  const notApplicable = t("meetingNotes.distribute.pdf.notApplicable");
  const tableData = items.map((item, index) => {
    const itemType = item.types?.[0];
    const isInfoOrDecision =
      itemType === MeetingItemType.Decision ||
      itemType === "decision" ||
      itemType === MeetingItemType.Info ||
      itemType === "info";
    const actor = item.actor || "";
    const actorFormatted = actor.includes("@")
      ? actor.replace("@", "\n@")
      : actor;
    return [
      index + 1,
      getMeetingItemTypeLabel(t, itemType),
      item.category || notApplicable,
      item.content || item.description || notApplicable,
      actorFormatted || notApplicable,
      item.dueDate
        ? new Date(item.dueDate).toLocaleDateString(lang || undefined)
        : notApplicable,
      isInfoOrDecision
        ? notApplicable
        : getMeetingItemStatusLabel(t, item.status),
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [
      [
        t("meetingNotes.distribute.pdf.tableHeaders.number"),
        t("meetingNotes.distribute.pdf.tableHeaders.type"),
        t("meetingNotes.distribute.pdf.tableHeaders.category"),
        t("meetingNotes.distribute.pdf.tableHeaders.content"),
        t("meetingNotes.distribute.pdf.tableHeaders.responsible"),
        t("meetingNotes.distribute.pdf.tableHeaders.dueDate"),
        t("meetingNotes.distribute.pdf.tableHeaders.status"),
      ],
    ],
    body: tableData,
    theme: "striped",
    headStyles: {
      fillColor: [36, 44, 99],
      textColor: 255,
      fontStyle: "bold",
    },
    styles: {
      fontSize: 7.5,
      cellPadding: 3,
    },
    columnStyles: {
      0: { cellWidth: 14 },
      1: { cellWidth: 26 },
      2: { cellWidth: 32 },
      3: { cellWidth: "auto" },
      4: { cellWidth: 36 },
      5: { cellWidth: 28 },
      6: { cellWidth: 28 },
    },
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(156, 163, 175);
    const footerText = t("meetingNotes.distribute.pdf.footer", {
      current: i,
      total: pageCount,
    });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const footerY = pageH - 10;
    doc.text(footerText, pageW / 2, footerY, { align: "center" });
    const textW = doc.getTextWidth(footerText);
    doc.link(pageW / 2 - textW / 2, footerY - 3, textW, 4, {
      url: "https://pinnacle.ceo",
    });
  }

  const fileName = `${meeting?.title || t("meetingNotes.distribute.pdf.fileNameFallback")}_${meeting?.date}_${t("meetingNotes.distribute.pdf.fileNameSuffix")}.pdf`;
  return { doc, fileName };
};

const generatePDF = (
  t: TFunction,
  meeting: SelectedMeeting | null,
  items: MeetingItem[],
  lang: string,
) => {
  const { doc, fileName } = buildMinutesDoc(t, meeting, items, lang);
  doc.save(fileName);
};

export interface BuildMailtoArgs {
  emailTemplate: string;
  participants: Participant[];
  creatorEmail: string | undefined;
  meetingId: string | undefined;
  meetingTitle: string;
  publishedOn: Date;
  baseUrl: string;
  lang: string;
  items: MeetingItem[];
  t: TFunction;
}

export const buildMailtoHref = ({
  emailTemplate,
  participants,
  creatorEmail,
  meetingId,
  meetingTitle,
  publishedOn,
  baseUrl,
  lang,
  items,
  t,
}: BuildMailtoArgs): string | undefined => {
  const toUsers = participants.filter(
    (p) => p.emailDistribution === "to" && p.email !== creatorEmail,
  );
  const ccUsers = participants.filter(
    (p) => p.emailDistribution === "cc" && p.email !== creatorEmail,
  );

  if (toUsers.length === 0 && ccUsers.length === 0) return undefined;

  const toList = toUsers.map((p) => p.email).join(";");
  const ccList = ccUsers.map((p) => p.email).join(";");
  const publishedDateLabel = publishedOn.toLocaleDateString(lang || undefined);
  const ccParam = ccList ? `&cc=${encodeURIComponent(ccList)}` : "";
  const creatorLabel =
    creatorEmail ?? t("meetingNotes.distribute.email.fallbackMeetingReference");

  let subject: string;
  let body: string;

  if (emailTemplate === "summary") {
    subject = t("mail_template.summary_subject", {
      meetingName: meetingTitle,
      published_on: publishedDateLabel,
    });
    body = t("mail_template.summary_content", {
      baseUrl,
      meetingId,
      creator: creatorLabel,
      meetingName: meetingTitle,
    });
  } else if (emailTemplate === "action-items") {
    const taskList = items
      .filter((i) => i.content || i.description)
      .map((i) => {
        const due = i.dueDate
          ? new Date(i.dueDate).toLocaleDateString(lang || undefined)
          : "";
        const parts = [`- ${i.content || i.description}`];
        if (i.actor) parts.push(i.actor);
        if (due) parts.push(due);
        return parts.join(" | ");
      })
      .join("\n");
    subject = t("mail_template.action_items_subject", {
      meetingName: meetingTitle,
      published_on: publishedDateLabel,
    });
    body = t("mail_template.action_items_content", {
      meetingName: meetingTitle,
      baseUrl,
      meetingId,
      creator: creatorLabel,
      taskList,
    });
  } else {
    subject = t("mail_template.meeting_minutes_subject", {
      meetingName: meetingTitle,
      published_on: publishedDateLabel,
    });
    body = t("mail_template.meeting_minutes_content", {
      baseUrl,
      meetingId,
      creator: creatorLabel,
    });
  }

  return (
    `mailto:${encodeURIComponent(toList)}?subject=${encodeURIComponent(subject)}` +
    `${ccParam}&body=${encodeURIComponent(body)}`
  );
};

const DistributeStep = ({
  onBack,
  onDistributeComplete,
  selectedMeeting,
  items,
  distributeRef,
  onMeetingChange,
  onMeetingStatusChange,
  onError,
}: DistributeStepProps) => {
  const { t, i18n } = useTranslation();
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [downloadPdf, setDownloadPdf] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [emailTemplate, setEmailTemplate] = useState("standard");
  const [attachPdf, setAttachPdf] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  // Publishing options state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showAddRow, setShowAddRow] = useState(false);
  const addInputRef = useRef<HTMLInputElement>(null);
  const emailTemplates = [
    {
      value: "none",
      label: t("meetingNotes.distribute.emailTemplates.none"),
    },
    {
      value: "standard",
      label: t("meetingNotes.distribute.emailTemplates.standard"),
    },
    {
      value: "action-items",
      label: t("meetingNotes.distribute.emailTemplates.actionItems"),
    },
  ];

  const buildEmailDraft = useCallback(
    (publishedOn: Date) => {
      const href = buildMailtoHref({
        emailTemplate,
        participants,
        creatorEmail: selectedMeeting?.originalMeeting?.creator,
        meetingId: selectedMeeting?.id,
        meetingTitle:
          selectedMeeting?.title ??
          t("meetingNotes.distribute.email.fallbackMeetingTitle"),
        publishedOn,
        baseUrl: config.web.baseUrl,
        lang: i18n.language,
        items,
        t,
      });
      if (href) {
        window.location.href = href;
      }
    },
    [emailTemplate, i18n.language, items, participants, selectedMeeting, t],
  );

  const startDistribution = useCallback(async () => {
    const creatorEmail = selectedMeeting?.originalMeeting?.creator;
    const viewers = participants
      .filter((participant) => participant.accessRights)
      .map((participant) => participant.email)
      .filter((email) => email && email.includes("@"));

    if (
      creatorEmail &&
      !viewers.some(
        (viewer) => viewer.toLowerCase() === creatorEmail.toLowerCase(),
      )
    ) {
      viewers.push(creatorEmail);
    }

    const users: ApiUser[] = participants
      .filter(
        (participant) => participant.email && participant.email.includes("@"),
      )
      .map((participant) => ({
        email: participant.email,
        role: UserRole.Actor,
      }));

    const publishedOn = new Date();

    try {
      setProgressOpen(true);
      setProgressValue(20);

      const published = await onMeetingStatusChange(MeetingStatus.Published, {
        viewers,
        publishedOn,
      });

      if (!published) {
        setProgressOpen(false);
        return;
      }

      setProgressValue(60);

      if (users.length > 0) {
        await bulkCreateUsers(users);
      }

      setProgressValue(85);

      // Always generate + upload meeting minutes PDF. Best-effort — failed upload
      // should not block distribution.
      const meetingId = selectedMeeting?.id;
      if (meetingId) {
        try {
          const { doc, fileName } = buildMinutesDoc(
            t,
            selectedMeeting,
            items,
            i18n.language,
          );
          const pdfBlob = doc.output("blob");
          await uploadMeetingMinutes(meetingId, pdfBlob, fileName);
        } catch (uploadErr) {
          console.error(
            "[DistributeStep] failed to upload meeting minutes PDF",
            uploadErr,
          );
          onError(t("meetingNotes.distribute.errors.uploadMinutes"), uploadErr);
        }
      }

      if (emailTemplate !== "none") {
        buildEmailDraft(publishedOn);
      }

      setProgressValue(100);
      setTimeout(() => {
        if (downloadPdf || (attachPdf && emailTemplate !== "none")) {
          try {
            generatePDF(t, selectedMeeting, items, i18n.language);
          } catch (pdfError) {
            onError(t("meetingNotes.distribute.errors.generatePdf"), pdfError);
          }
        }
        setProgressOpen(false);
        onDistributeComplete?.();
      }, 1000);
    } catch (error) {
      setProgressOpen(false);
      onError(t("meetingNotes.distribute.errors.distribute"), error);
    }
  }, [
    attachPdf,
    buildEmailDraft,
    downloadPdf,
    emailTemplate,
    i18n.language,
    items,
    onDistributeComplete,
    onError,
    onMeetingStatusChange,
    participants,
    selectedMeeting,
    t,
  ]);

  // Track the original participants to prevent resetting local changes when the meeting status updates during distribution
  const initializedParticipantsRef = useRef<string | null>(null);

  // Initialize participants from meeting
  useEffect(() => {
    if (selectedMeeting?.originalMeeting) {
      const currentParticipantsStr = JSON.stringify(
        selectedMeeting.originalMeeting.participants || [],
      );

      // If we already initialized for these exact meeting participants, do nothing
      if (
        initializedParticipantsRef.current === currentParticipantsStr &&
        participants.length > 0
      ) {
        return;
      }

      const creatorEmail = selectedMeeting.originalMeeting.creator;
      const meetingParticipants =
        selectedMeeting.originalMeeting.participants || [];
      const allParticipants: Participant[] = [];

      // Creator/organizer first, marked as author
      if (creatorEmail) {
        allParticipants.push({
          id: "author",
          email: creatorEmail,
          emailDistribution: "to" as const,
          accessRights: true,
          isAuthor: true,
        });
      }

      // Remaining participants (skip duplicates of creator)
      meetingParticipants.forEach((email, index) => {
        if (creatorEmail && email.toLowerCase() === creatorEmail.toLowerCase())
          return;
        const hasEmail = isEmailFormat(email);
        allParticipants.push({
          id: String(index + 1),
          email,
          emailDistribution: hasEmail ? ("to" as const) : ("none" as const),
          accessRights: hasEmail,
        });
      });

      setParticipants(allParticipants);
      initializedParticipantsRef.current = currentParticipantsStr;
      return;
    }

    setParticipants([]);
    initializedParticipantsRef.current = null;
  }, [selectedMeeting, participants.length]);

  // Expose distribute action to parent via ref
  useEffect(() => {
    if (distributeRef) {
      distributeRef.current = () => {
        void startDistribution();
      };
    }
    return () => {
      if (distributeRef) distributeRef.current = null;
    };
  }, [distributeRef, startDistribution]);

  // Publishing options helpers
  const authorDomain = useMemo(() => {
    const author = participants.find((p) => p.isAuthor);
    return author ? author.email.split("@")[1]?.toLowerCase() : null;
  }, [participants]);

  const hasEligibleParticipants = useMemo(
    () => participants.some((p) => isEmailFormat(p.email)),
    [participants],
  );

  const allEmailTo = useMemo(() => {
    const eligible = participants.filter((p) => isEmailFormat(p.email));
    return (
      eligible.length > 0 && eligible.every((p) => p.emailDistribution === "to")
    );
  }, [participants]);

  const allAccessSelected = useMemo(() => {
    const eligible = participants.filter((p) => isEmailFormat(p.email));
    return eligible.length > 0 && eligible.every((p) => p.accessRights);
  }, [participants]);

  const toggleAllEmail = () => {
    const newVal: Participant["emailDistribution"] = allEmailTo ? "none" : "to";
    setParticipants((prev) =>
      prev.map((p) =>
        isEmailFormat(p.email) ? { ...p, emailDistribution: newVal } : p,
      ),
    );
  };

  const toggleAllAccess = () => {
    const newVal = !allAccessSelected;
    setParticipants((prev) =>
      prev.map((p) =>
        isEmailFormat(p.email) ? { ...p, accessRights: newVal } : p,
      ),
    );
  };

  const cycleEmailTo = (id: string, state: "to" | "cc" | "none") => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.id === id && isEmailFormat(p.email)
          ? { ...p, emailDistribution: state }
          : p,
      ),
    );
  };

  const toggleAccess = (id: string) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.id === id && isEmailFormat(p.email)
          ? { ...p, accessRights: !p.accessRights }
          : p,
      ),
    );
  };

  const requestRemove = (id: string) => {
    setPendingRemoveId(id);
    setRemoveDialogOpen(true);
  };

  const confirmRemove = () => {
    if (pendingRemoveId)
      setParticipants((prev) => prev.filter((p) => p.id !== pendingRemoveId));
    setRemoveDialogOpen(false);
    setPendingRemoveId(null);
  };

  const cancelRemove = () => {
    setRemoveDialogOpen(false);
    setPendingRemoveId(null);
  };

  const handleAddParticipant = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (
      participants.some((p) => p.email.toLowerCase() === trimmed.toLowerCase())
    ) {
      setEmailError(t("meetingNotes.distribute.table.alreadyAdded"));
      return;
    }
    const hasEmail = isEmailFormat(trimmed);
    setParticipants((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        email: trimmed,
        emailDistribution: hasEmail ? "to" : "none",
        accessRights: hasEmail,
      },
    ]);
    setNewEmail("");
    setEmailError("");
    // Keep row open and refocus for the next entry
    setTimeout(() => addInputRef.current?.focus(), 0);
  };

  const handleAddKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === "Tab") && newEmail.trim()) {
      e.preventDefault();
      handleAddParticipant();
    }
    if (e.key === "Escape") {
      setShowAddRow(false);
      setNewEmail("");
      setEmailError("");
    }
  };

  const chipSx = (active: boolean) => ({
    fontSize: 12,
    fontWeight: 500,
    minWidth: 148,
    justifyContent: "center",
    borderRadius: "6px",
    borderColor: active ? undefined : "#353D7A",
    color: active ? "#fff" : "#353D7A",
    backgroundColor: active ? "#242C63" : "transparent",
    "&:hover": { backgroundColor: active ? "#353D7A" : "rgba(36,44,99,0.08)" },
  });

  const accessTooltip = (p: Participant, hasEmail: boolean): string => {
    if (!hasEmail)
      return t("meetingNotes.distribute.table.tooltips.noEmailContact");
    if (p.isAuthor)
      return t("meetingNotes.distribute.table.tooltips.authorAlwaysAccess");
    return p.accessRights
      ? t("meetingNotes.distribute.table.tooltips.hasAccess")
      : t("meetingNotes.distribute.table.tooltips.grantAccess");
  };

  const noAccessTooltip = (p: Participant, hasEmail: boolean): string => {
    if (!hasEmail)
      return t("meetingNotes.distribute.table.tooltips.noEmailContact");
    if (p.isAuthor)
      return t("meetingNotes.distribute.table.tooltips.authorAlwaysAccess");
    return p.accessRights
      ? t("meetingNotes.distribute.table.tooltips.revokeAccess")
      : t("meetingNotes.distribute.table.tooltips.noAccess");
  };

  return (
    <>
      {/* Progress Dialog */}
      <Dialog
        open={progressOpen}
        onClose={() => {
          /* Prevent closing during progress */
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              p: 3,
              textAlign: "center",
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#242C63",
            mb: 2,
          }}
        >
          {t("meetingNotes.distribute.progress.inProgressTitle")}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#E5E7EB",
            "& .MuiLinearProgress-bar": { backgroundColor: "#242C63" },
          }}
        />
        <Typography sx={{ fontSize: "0.875rem", color: "#6B7280", mt: 2 }}>
          {t("meetingNotes.distribute.progress.percentComplete", {
            value: progressValue,
          })}
        </Typography>
      </Dialog>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Meeting info header */}
        {selectedMeeting && (
          <Box
            sx={{
              py: 1,
              backgroundColor: "#F8F9FC",
              borderBottom: "1px solid #E5E7EB",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
            }}
          >
            <EditableMeetingInfo
              meeting={selectedMeeting}
              onMeetingChange={onMeetingChange}
              layout="compact"
            />
          </Box>
        )}

        {/* Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Container 1: Invitees */}
          <Card
            variant="outlined"
            sx={{
              borderColor: "#D1D5DB",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography sx={sectionLabelSx}>
                {t("meetingNotes.distribute.sections.invitees")}
              </Typography>
              <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                {participants.length > 0 ? (
                  participants.map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 0.75,
                        borderBottom: "1px solid #F3F4F6",
                        "&:last-child": { borderBottom: 0 },
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "0.875rem", color: "#1A2048" }}
                      >
                        {p.email}
                      </Typography>
                      {p.isAuthor && (
                        <Typography
                          sx={{
                            fontSize: "0.6875rem",
                            fontWeight: 500,
                            color: "#6B7280",
                            border: "1px solid #C3C7CC",
                            borderRadius: "9999px",
                            px: 1,
                            py: 0.25,
                            flexShrink: 0,
                          }}
                        >
                          {t("meetingNotes.distribute.author")}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ fontSize: "0.875rem", color: "#6B7280" }}>
                    {t("meetingNotes.distribute.noParticipants")}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Publishing options table */}
          <Card
            variant="outlined"
            sx={{
              borderColor: "#D1D5DB",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              {/* Table header */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 180px 160px 48px",
                  alignItems: "center",
                  gap: 1,
                  borderBottom: "1px solid #E5E7EB",
                  borderTop: "1px solid #E5E7EB",
                  px: 2.5,
                  py: 1.5,
                  backgroundColor: "#F8FAFC",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#64748B",
                  }}
                >
                  {t("meetingNotes.distribute.table.participant")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#64748B",
                    }}
                  >
                    {t("meetingNotes.distribute.table.publishing")}
                  </Typography>
                  <Chip
                    icon={<MarkEmailReadIcon sx={{ fontSize: 15 }} />}
                    label={
                      allEmailTo
                        ? t("meetingNotes.distribute.table.removeAllEmails")
                        : t("meetingNotes.distribute.table.emailAll")
                    }
                    size="small"
                    onClick={toggleAllEmail}
                    disabled={!hasEligibleParticipants}
                    variant={allEmailTo ? "filled" : "outlined"}
                    sx={chipSx(allEmailTo)}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#64748B",
                    }}
                  >
                    {t("meetingNotes.distribute.table.access")}
                  </Typography>
                  <Chip
                    icon={<MenuBookIcon sx={{ fontSize: 15 }} />}
                    label={
                      allAccessSelected
                        ? t("meetingNotes.distribute.table.revokeAll")
                        : t("meetingNotes.distribute.table.grantAll")
                    }
                    size="small"
                    onClick={toggleAllAccess}
                    disabled={!hasEligibleParticipants}
                    variant={allAccessSelected ? "filled" : "outlined"}
                    sx={chipSx(allAccessSelected)}
                  />
                </Box>
                <span />
              </Box>

              {/* Participant rows */}
              {participants.map((p) => {
                const hasEmail = isEmailFormat(p.email);
                const distributionLocked = !hasEmail;
                const accessLocked = p.isAuthor || !hasEmail;
                return (
                  <Box
                    key={p.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 180px 160px 48px",
                      alignItems: "center",
                      gap: 1,
                      borderBottom: "1px solid #E5E7EB",
                      px: 2.5,
                      py: 1.25,
                      "&:hover": { backgroundColor: "rgba(248,250,252,0.6)" },
                      transition: "background-color 120ms",
                    }}
                  >
                    {/* Email + badges */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: "#1A2048",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.email}
                      </Typography>
                      {p.isAuthor && (
                        <Typography
                          sx={{
                            flexShrink: 0,
                            fontSize: "0.6875rem",
                            fontWeight: 500,
                            color: "#6B7280",
                            border: "1px solid #C3C7CC",
                            borderRadius: "9999px",
                            px: 1,
                            py: 0.25,
                          }}
                        >
                          {t("meetingNotes.distribute.author")}
                        </Typography>
                      )}
                      {!p.isAuthor &&
                        authorDomain &&
                        p.email.split("@")[1]?.toLowerCase() !==
                          authorDomain && (
                          <Chip
                            label={t("meetingNotes.distribute.table.external")}
                            size="small"
                            variant="outlined"
                            sx={{
                              flexShrink: 0,
                              fontSize: 11,
                              height: 22,
                              borderColor: "#E0C97F",
                              color: "#8B6914",
                              backgroundColor: "#FDF8EC",
                              fontWeight: 600,
                            }}
                          />
                        )}
                    </Box>

                    {/* Email distribution icons */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.25,
                      }}
                    >
                      <Tooltip
                        title={
                          distributionLocked
                            ? t(
                                "meetingNotes.distribute.table.tooltips.noEmailContact",
                              )
                            : t(
                                "meetingNotes.distribute.table.tooltips.emailTo",
                              )
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => cycleEmailTo(p.id, "to")}
                            disabled={distributionLocked}
                            sx={{
                              color:
                                p.emailDistribution === "to"
                                  ? "#242C63"
                                  : "#C3C7CC",
                              "&.Mui-disabled": { color: "#C3C7CC" },
                            }}
                          >
                            <MarkEmailReadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          distributionLocked
                            ? t(
                                "meetingNotes.distribute.table.tooltips.noEmailContact",
                              )
                            : t(
                                "meetingNotes.distribute.table.tooltips.emailCc",
                              )
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => cycleEmailTo(p.id, "cc")}
                            disabled={distributionLocked}
                            sx={{
                              color:
                                p.emailDistribution === "cc"
                                  ? "#242C63"
                                  : "#C3C7CC",
                              "&.Mui-disabled": { color: "#C3C7CC" },
                            }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          distributionLocked
                            ? t(
                                "meetingNotes.distribute.table.tooltips.noEmailContact",
                              )
                            : t(
                                "meetingNotes.distribute.table.tooltips.noEmail",
                              )
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => cycleEmailTo(p.id, "none")}
                            disabled={distributionLocked}
                            sx={{
                              color:
                                p.emailDistribution === "none" &&
                                !distributionLocked
                                  ? "#A92A2A"
                                  : "#C3C7CC",
                              "&.Mui-disabled": { color: "#C3C7CC" },
                            }}
                          >
                            <UnsubscribeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    {/* Access icons */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.25,
                      }}
                    >
                      <Tooltip title={accessTooltip(p, hasEmail)}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => !accessLocked && toggleAccess(p.id)}
                            disabled={accessLocked}
                            sx={{
                              color: p.accessRights ? "#242C63" : "#C3C7CC",
                              "&.Mui-disabled": {
                                color: p.isAuthor ? "#242C63" : "#C3C7CC",
                              },
                            }}
                          >
                            <MenuBookIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={noAccessTooltip(p, hasEmail)}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => !accessLocked && toggleAccess(p.id)}
                            disabled={accessLocked}
                            sx={{
                              color:
                                !p.accessRights && !accessLocked
                                  ? "#A92A2A"
                                  : "#C3C7CC",
                              "&.Mui-disabled": { color: "#C3C7CC" },
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    {/* Remove */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Tooltip
                        title={
                          p.isAuthor
                            ? t(
                                "meetingNotes.distribute.table.tooltips.authorCannotRemove",
                              )
                            : t(
                                "meetingNotes.distribute.table.tooltips.removeParticipant",
                              )
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => !p.isAuthor && requestRemove(p.id)}
                            disabled={p.isAuthor}
                            sx={{
                              color: "#C3C7CC",
                              "&:hover": { color: "#A92A2A" },
                              "&.Mui-disabled": { color: "#E0E0E0" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}

              {participants.length === 0 && !showAddRow && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    fontSize: "0.875rem",
                    color: "#828CA1",
                  }}
                >
                  {t("meetingNotes.distribute.table.emptyParticipants")}
                </Box>
              )}

              {/* Add participant row */}
              {showAddRow ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 180px 160px 48px",
                    alignItems: "center",
                    gap: 1,
                    px: 2.5,
                    py: 1.25,
                    backgroundColor: "rgba(248,250,252,0.6)",
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <input
                      ref={addInputRef}
                      type="text"
                      value={newEmail}
                      onChange={(e) => {
                        setNewEmail(e.target.value);
                        setEmailError("");
                      }}
                      onKeyDown={handleAddKeyDown}
                      placeholder={t(
                        "meetingNotes.editableMeetingInfo.participantPlaceholder",
                      )}
                      autoFocus
                      style={{
                        fontSize: "0.875rem",
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: "4px",
                        padding: "6px 12px",
                        outline: "none",
                      }}
                    />
                    {emailError && (
                      <Typography
                        sx={{ fontSize: "0.75rem", color: "#EF4444" }}
                      >
                        {emailError}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={handleAddParticipant}
                      variant="contained"
                      disableElevation
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        backgroundColor: "#242C63",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#353D7A" },
                      }}
                    >
                      {t("meetingNotes.distribute.table.add")}
                    </Button>
                  </Box>
                  <span />
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowAddRow(false);
                        setNewEmail("");
                        setEmailError("");
                      }}
                      sx={{ color: "#C3C7CC" }}
                    >
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2.5,
                    py: 1.25,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(248,250,252,0.6)" },
                    transition: "background-color 120ms",
                  }}
                  onClick={() => {
                    setShowAddRow(true);
                    setTimeout(() => addInputRef.current?.focus(), 50);
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 18, color: "#242C63" }} />
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: "#242C63",
                      fontWeight: 500,
                    }}
                  >
                    {t("meetingNotes.distribute.table.addParticipant")}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <ConfirmationPanel
            showModal={removeDialogOpen}
            hideModal={cancelRemove}
            confirmModal={confirmRemove}
            action="delete"
            type="user"
            entry={participants.find((p) => p.id === pendingRemoveId)}
          />

          {/* Email template & PDF attachment */}
          <Card
            variant="outlined"
            sx={{
              borderColor: "#D1D5DB",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Email template selector */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <EmailOutlinedIcon
                      sx={{ fontSize: 20, color: "#64748B" }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "#1A2048",
                        }}
                      >
                        {t("meetingNotes.distribute.emailTemplateTitle")}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.8125rem", color: "#64748B" }}
                      >
                        {t("meetingNotes.distribute.emailTemplateDescription")}
                      </Typography>
                    </Box>
                  </Box>
                  <Select
                    value={emailTemplate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__create_new__") {
                        setCreateTemplateOpen(true);
                        return;
                      }
                      setEmailTemplate(val);
                    }}
                    size="small"
                    sx={{
                      minWidth: 220,
                      fontSize: "0.8125rem",
                      borderRadius: "4px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E5E7EB",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#242C63",
                      },
                    }}
                  >
                    {emailTemplates.map((t) => (
                      <MenuItem
                        key={t.value}
                        value={t.value}
                        sx={{ fontSize: "0.8125rem" }}
                      >
                        {t.label}
                      </MenuItem>
                    ))}
                    {/* <Divider sx={{ my: 0.5 }} />
                    <MenuItem
                      value="__create_new__"
                      disabled
                      sx={{ fontSize: "0.8125rem", color: "#242C63" }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <AddOutlinedIcon
                          sx={{ fontSize: 16, color: "#242C63" }}
                        />
                      </ListItemIcon>
                      {t("meetingNotes.distribute.createNewTemplate")}
                    </MenuItem> */}
                  </Select>
                </Box>

                {/* Attach PDF toggle */}
                {/* {isEmailSelected && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      pl: 4,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <AttachFileOutlinedIcon
                        sx={{ fontSize: 18, color: "#64748B" }}
                      />
                      <Typography
                        sx={{ fontSize: "0.8125rem", color: "#1A2048" }}
                      >
                        {t("meetingNotes.distribute.attachPdf")}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={attachPdf}
                          onChange={(e) => setAttachPdf(e.target.checked)}
                          disabled={true}
                          sx={switchSx}
                        />
                      }
                      label=""
                      sx={{ mr: 0 }}
                    />
                  </Box>
                )} */}
              </Box>
            </CardContent>
          </Card>

          {/* Container 3: Download PDF */}
          <Card
            variant="outlined"
            sx={{
              borderColor: "#D1D5DB",
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <PictureAsPdfOutlinedIcon
                    sx={{ fontSize: 20, color: "#64748B" }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1A2048",
                      }}
                    >
                      {t("meetingNotes.distribute.downloadPdfTitle")}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.8125rem", color: "#64748B" }}
                    >
                      {t("meetingNotes.distribute.downloadPdfDescription")}
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={downloadPdf}
                      onChange={(e) => setDownloadPdf(e.target.checked)}
                      sx={switchSx}
                    />
                  }
                  label=""
                  sx={{ mr: 0 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Footer */}
          <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{
                textTransform: "none",
                borderColor: "#E5E7EB",
                color: "#1A2048",
                "&:hover": { borderColor: "#C3C7CC" },
              }}
            >
              {t("button.back")}
            </Button>
          </Box>
        </Box>
      </Box>
      {/* Create new template dialog */}
      <Dialog
        open={createTemplateOpen}
        onClose={() => setCreateTemplateOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              boxShadow: "0px 16px 40px -8px rgba(36, 44, 99, 0.12)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E5E7EB",
            py: 1.5,
            px: 3,
            fontSize: "1rem",
            fontWeight: 600,
            color: "#242C63",
          }}
        >
          {t("meetingNotes.distribute.createEmailTemplateTitle")}
          <IconButton
            onClick={() => setCreateTemplateOpen(false)}
            size="small"
            sx={{ color: "#64748B" }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            py: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{ fontSize: "0.875rem", color: "#64748B", textAlign: "center" }}
          >
            {t("meetingNotes.distribute.createEmailTemplateDescription")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={() => setCreateTemplateOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "#E5E7EB",
              color: "#1A2048",
              "&:hover": { borderColor: "#C3C7CC" },
            }}
          >
            {t("button.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DistributeStep;
